import { AppstoreAddOutlined, DeleteOutlined, DownOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Dropdown, Menu, Space } from 'antd';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type TtbPaymentModuleSpecsFragment } from '../../../../api/fragments/TtbPaymentModuleSpecs';
import { type PermissionValues } from '../../../../utilities/hasPermissions';
import useDeleteModule from '../useDeleteModule';
import { useTtbPaymentSettingsDrawer } from './TtbPaymentSettingsDrawer';
import { useUpdateTtbPaymentModuleDrawer } from './UpdateTtbPaymentModuleDrawer';

export type TtbPaymentModuleActionsProps = {
    module: TtbPaymentModuleSpecsFragment;
    permissions: Pick<PermissionValues, 'hasDeletePermission' | 'hasUpdatePermission'>;
};

const TtbPaymentModuleActions = ({ module, permissions }: TtbPaymentModuleActionsProps) => {
    const { t } = useTranslation(['ttbPaymentModuleDetails', 'moduleDetails']);
    const deleteModule = useDeleteModule(module.id);
    const addTtbPaymentSettingsDrawer = useTtbPaymentSettingsDrawer(module.id);

    const initialValues = useMemo(() => pick(['displayName'], module), [module]);
    const updatePayGatePaymentModuleDrawer = useUpdateTtbPaymentModuleDrawer(module.id, initialValues);

    const { hasUpdatePermission, hasDeletePermission } = permissions;

    const items = [
        hasUpdatePermission && {
            label: t('ttbPaymentModuleDetails:mainActionButtons.updateSpecs'),
            key: 'updateSpecs',
            icon: <EditOutlined />,
            onClick: updatePayGatePaymentModuleDrawer.open,
        },
        hasUpdatePermission && {
            label: t('ttbPaymentModuleDetails:mainActionButtons.addSetting'),
            key: 'addSetting',
            icon: <AppstoreAddOutlined />,
            onClick: addTtbPaymentSettingsDrawer.open,
        },
        hasDeletePermission && {
            label: t('ttbPaymentModuleDetails:mainActionButtons.delete'),
            key: 'delete',
            icon: <DeleteOutlined />,
            onClick: deleteModule,
            danger: true,
        },
    ];

    return (
        <>
            <Dropdown overlay={<Menu items={items} />}>
                <Button type="primary">
                    <Space>
                        <span>{t('ttbPaymentModuleDetails:mainActionButtons.actions')}</span>
                        <DownOutlined />
                    </Space>
                </Button>
            </Dropdown>
            {addTtbPaymentSettingsDrawer.render()}
            {updatePayGatePaymentModuleDrawer.render()}
        </>
    );
};

export default TtbPaymentModuleActions;
